/**
 * Menú Cobranza del Portal Condominio: pestañas Cartera vencida, Pagos y Cargos (decisión de Eduardo,
 * 2026-10-15: "Cargos", "Pagos y Conciliación" y "Cobranza" mostraban lo mismo desde tres menús).
 * Pagos y cargos los arma el servidor (`get_condominio_pagos` / `get_condominio_cargos`,
 * migración 20261015230000) por periodo; aquí solo se normalizan, filtran y suman. Sin dependencias
 * de Supabase: se prueban solas.
 */
import { formatCuentaMantenimientoId } from "@/utils/cuentaCobranzaUtils";
import { campoBusquedaUnidad, centavos } from "./unidades";

// ─── Periodo ─────────────────────────────────────────────────────────────────

export type PeriodoCobranza = "este_mes" | "mes_anterior" | "ultimos_3" | "este_anio" | "todo";

export const PERIODOS_COBRANZA: { value: PeriodoCobranza; label: string }[] = [
  { value: "este_mes", label: "Este mes" },
  { value: "mes_anterior", label: "Mes anterior" },
  { value: "ultimos_3", label: "Últimos 3 meses" },
  { value: "este_anio", label: "Este año" },
  { value: "todo", label: "Todo" },
];

export const esPeriodoCobranza = (v: string): v is PeriodoCobranza => PERIODOS_COBRANZA.some((p) => p.value === v);

const iso = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const finDeMes = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** Hoy en la Ciudad de México como `YYYY-MM-DD` (el servidor usa la misma zona). */
export const hoyCdmx = (ahora = new Date()): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City" }).format(ahora);

/** Rango `[desde, hasta]` del periodo (inclusive); `null` en cada extremo = sin límite. */
export function rangoPeriodo(p: PeriodoCobranza, hoy = hoyCdmx()): { desde: string | null; hasta: string | null } {
  const [y, m] = hoy.split("-").map(Number);
  switch (p) {
    case "este_mes":
      return { desde: iso(y, m, 1), hasta: iso(y, m, finDeMes(y, m)) };
    case "mes_anterior": {
      const [py, pm] = m === 1 ? [y - 1, 12] : [y, m - 1];
      return { desde: iso(py, pm, 1), hasta: iso(py, pm, finDeMes(py, pm)) };
    }
    case "ultimos_3": {
      const [py, pm] = m <= 2 ? [y - 1, m + 10] : [y, m - 2];
      return { desde: iso(py, pm, 1), hasta: iso(y, m, finDeMes(y, m)) };
    }
    case "este_anio":
      return { desde: iso(y, 1, 1), hasta: iso(y, 12, 31) };
    case "todo":
      return { desde: null, hasta: null };
  }
}

/** Meses de un rango en el formato de `PeriodoEtiqueta` (`año-mes` con el mes en base 0). */
export function mesesDeRango(desde: string | null, hasta: string | null): string[] | null {
  if (!desde || !hasta) return null;
  const out: string[] = [];
  let [y, m] = desde.split("-").map(Number);
  const [hy, hm] = hasta.split("-").map(Number);
  while (y < hy || (y === hy && m <= hm)) {
    out.push(`${y}-${m - 1}`);
    if (m === 12) { y += 1; m = 1; } else m += 1;
  }
  return out;
}

/** Meses del periodo en el formato de `PeriodoEtiqueta` (`año-mes` con el mes en base 0). */
export function mesesPeriodo(p: PeriodoCobranza, hoy = hoyCdmx()): string[] | null {
  const { desde, hasta } = rangoPeriodo(p, hoy);
  return mesesDeRango(desde, hasta);
}

/**
 * Periodo elegido con el selector estándar (`ui/selector-periodo`): año y mes (0 = sin elegir) o un
 * rango `desde`/`hasta` (`YYYY-MM-DD`, vacío = sin rango). Con rango, el rango manda.
 */
export interface PeriodoElegido { anio: number; mes: number; desde: string; hasta: string }

/** Rango `[desde, hasta]` (inclusive) de lo elegido; `null` en cada extremo = sin límite. */
export function rangoElegido(p: PeriodoElegido): { desde: string | null; hasta: string | null } {
  if (p.desde && p.hasta) return { desde: p.desde, hasta: p.hasta };
  if (!p.anio) return { desde: null, hasta: null };
  if (!p.mes) return { desde: iso(p.anio, 1, 1), hasta: iso(p.anio, 12, 31) };
  return { desde: iso(p.anio, p.mes, 1), hasta: iso(p.anio, p.mes, finDeMes(p.anio, p.mes)) };
}

/** Texto corto del periodo elegido para los subtítulos de los KPI. */
export function etiquetaElegido(p: PeriodoElegido): string {
  if (p.desde && p.hasta) return `${p.desde} al ${p.hasta}`;
  if (!p.anio) return "Todo el historial";
  return p.mes ? `${String(p.mes).padStart(2, "0")}/${p.anio}` : `Año ${p.anio}`;
}

// ─── Pagos ───────────────────────────────────────────────────────────────────

/** Conciliación de un pago contra los acuerdos activos de su cuenta (la decide el servidor). */
export type ConciliacionPago = "conciliado" | "parcial" | "sin_aplicar" | "excedido";

export const ETIQUETA_CONCILIACION: Record<ConciliacionPago, string> = {
  conciliado: "Conciliado",
  parcial: "Aplicado en parte",
  sin_aplicar: "Sin aplicar",
  excedido: "Aplicado de más",
};

export const DESCRIPCION_CONCILIACION: Record<ConciliacionPago, string> = {
  conciliado: "Todo el pago está aplicado a cuotas activas.",
  parcial: "Una parte del pago no está aplicada a una cuota activa: revisar antes de cobrar.",
  sin_aplicar: "El pago no está aplicado a ninguna cuota activa.",
  excedido: "Se aplicó más de lo que se pagó: hay aplicaciones duplicadas que corregir.",
};

export interface PagoCobranza {
  id: number;
  id_cuenta: number;
  unidad: string;
  propietario: string | null;
  fecha: string | null;
  monto: number;
  aplicado: number;
  conciliacion: ConciliacionPago;
  conceptos: number[];
  referencia: string | null;
  metodo: string | null;
  url_cep: string | null;
  url_recibo: string | null;
}

const CONCILIACIONES: ConciliacionPago[] = ["conciliado", "parcial", "sin_aplicar", "excedido"];

export function normalizarPago(f: Record<string, unknown>): PagoCobranza {
  const conc = String(f.conciliacion) as ConciliacionPago;
  return {
    id: Number(f.id),
    id_cuenta: Number(f.id_cuenta),
    unidad: String(f.unidad ?? ""),
    propietario: (f.propietario as string | null) ?? null,
    fecha: f.fecha ? String(f.fecha).slice(0, 10) : null,
    monto: Number(f.monto) || 0,
    aplicado: Number(f.aplicado) || 0,
    conciliacion: CONCILIACIONES.includes(conc) ? conc : "sin_aplicar",
    conceptos: Array.isArray(f.conceptos) ? (f.conceptos as unknown[]).map(Number) : [],
    referencia: (f.referencia as string | null) ?? null,
    metodo: (f.metodo as string | null) ?? null,
    url_cep: (f.url_cep as string | null) ?? null,
    url_recibo: (f.url_recibo as string | null) ?? null,
  };
}

/** Lo que falta por aplicar de un pago (o lo aplicado de más, negativo), en pesos. */
export const sinAplicarDe = (p: PagoCobranza): number => (centavos(p.monto) - centavos(p.aplicado)) / 100;

// ─── Cargos ──────────────────────────────────────────────────────────────────

export type EstatusCargo = "pagado" | "parcial" | "vencido" | "pendiente";

export const ETIQUETA_ESTATUS_CARGO: Record<EstatusCargo, string> = {
  pagado: "Pagado",
  parcial: "Pagado en parte",
  vencido: "Vencido",
  pendiente: "Por vencer",
};

export const CONCEPTO_CONDOMINIO: Record<number, string> = {
  11: "Mantenimiento",
  12: "Fondo de reserva",
  13: "Multa",
};

export interface CargoCobranza {
  id: number;
  id_cuenta: number;
  unidad: string;
  propietario: string | null;
  id_concepto: number;
  descripcion: string | null;
  fecha: string | null;
  monto: number;
  aplicado: number;
  resta: number;
  estatus: EstatusCargo;
}

const ESTATUS_CARGO: EstatusCargo[] = ["pagado", "parcial", "vencido", "pendiente"];

export function normalizarCargo(f: Record<string, unknown>): CargoCobranza {
  const est = String(f.estatus) as EstatusCargo;
  return {
    id: Number(f.id),
    id_cuenta: Number(f.id_cuenta),
    unidad: String(f.unidad ?? ""),
    propietario: (f.propietario as string | null) ?? null,
    id_concepto: Number(f.id_concepto),
    descripcion: (f.descripcion as string | null) ?? null,
    fecha: f.fecha ? String(f.fecha).slice(0, 10) : null,
    monto: Number(f.monto) || 0,
    aplicado: Number(f.aplicado) || 0,
    resta: Number(f.resta) || 0,
    estatus: ESTATUS_CARGO.includes(est) ? est : "pendiente",
  };
}

// ─── Búsqueda (mismo criterio que Departamentos) ─────────────────────────────

export type CampoBusquedaCobranza = "unidad" | "folio" | "propietario" | "referencia";
export type ModoBusquedaCobranza = "auto" | CampoBusquedaCobranza;

export const ETIQUETA_CAMPO_COBRANZA: Record<CampoBusquedaCobranza, string> = {
  unidad: "Unidad",
  folio: "Folio",
  propietario: "Propietario",
  referencia: "Clave de rastreo",
};

export const esModoBusquedaCobranza = (v: string): v is ModoBusquedaCobranza =>
  v === "auto" || v in ETIQUETA_CAMPO_COBRANZA;

const plano = (v: string | null | undefined): string =>
  (v ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
const digitos = (v: string) => v.replace(/\D/g, "");

/** Campo en modo Automático: como Departamentos; 10+ dígitos o letras con dígitos largos = clave de rastreo. */
export function campoBusquedaCobranza(texto: string, conReferencia: boolean): CampoBusquedaCobranza | null {
  const c = campoBusquedaUnidad(texto);
  if (c === "clabe") return conReferencia ? "referencia" : "folio";
  if (c === "propietario" && conReferencia && /\d{6,}/.test(texto)) return "referencia";
  return c;
}

/** ¿La fila coincide con la búsqueda? Fijar un campo busca exacto en unidad y folio. */
export function coincideBusqueda(
  f: { id_cuenta: number; unidad: string; propietario: string | null; referencia?: string | null },
  texto: string,
  modo: ModoBusquedaCobranza,
): boolean {
  const t = texto.trim();
  if (!t) return true;
  const campo = modo === "auto" ? campoBusquedaCobranza(t, f.referencia !== undefined) : modo;
  const exacto = modo !== "auto";
  switch (campo) {
    case "unidad":
      return exacto ? plano(f.unidad) === plano(t) : plano(f.unidad).startsWith(plano(t));
    case "folio": {
      const q = digitos(t);
      if (!q) return true;
      return exacto ? Number(q) === f.id_cuenta : digitos(formatCuentaMantenimientoId(f.id_cuenta)).includes(q);
    }
    case "referencia":
      return plano(f.referencia).includes(plano(t));
    case "propietario":
    default:
      return plano(f.propietario).includes(plano(t));
  }
}

// ─── Totales ─────────────────────────────────────────────────────────────────

export function kpisPagos(pagos: PagoCobranza[]) {
  let monto = 0, aplicado = 0, porAplicar = 0, conPendiente = 0, excedidos = 0;
  for (const p of pagos) {
    monto += centavos(p.monto);
    aplicado += centavos(p.aplicado);
    const falta = centavos(p.monto) - centavos(p.aplicado);
    if (falta > 0) { porAplicar += falta; conPendiente += 1; }
    if (falta < 0) excedidos += 1;
  }
  return { pagos: pagos.length, monto: monto / 100, aplicado: aplicado / 100, porAplicar: porAplicar / 100, conPendiente, excedidos };
}

export function kpisCargos(cargos: CargoCobranza[]) {
  let monto = 0, aplicado = 0, vencido = 0, multas = 0, cargosVencidos = 0;
  for (const c of cargos) {
    monto += centavos(c.monto);
    aplicado += centavos(c.aplicado);
    if (c.estatus === "vencido" || (c.estatus === "parcial" && c.fecha != null && c.fecha < hoyCdmx())) {
      vencido += centavos(c.resta);
      cargosVencidos += 1;
    }
    if (c.id_concepto === 13) multas += centavos(c.monto);
  }
  return {
    cargos: cargos.length, monto: monto / 100, aplicado: aplicado / 100, vencido: vencido / 100,
    cargosVencidos, multas: multas / 100, avance: monto > 0 ? aplicado / monto : 0,
  };
}
